import "./TimeLine.scss";

import * as d3 from "d3";

import React, { Component } from "react";

import { AVIActionButton } from "./../AVIActionButton/AVIActionButton";
import { NumUtils } from "./../ChartUtils/NumUtils";
import { PosUtils } from "../ChartUtils/PosUtils";
import PropTypes from "prop-types";
import { TimeUtils } from "../ChartUtils/TimeUtils";

export class TimeLine extends Component {
  // Property pointing to time marker circle on time line
  timeMarker = null;

  // The x Scale used to resolve time to pixels and vice versa
  xScale = null;

  // Timeout that will be used for playing back animations
  timeout = null;

  constructor(props) {
    super(props);
    var { fromTime, toTime, startTime, initialSpeed } = props;
    console.log(fromTime, TimeUtils.dateToUTC(fromTime));

    fromTime = fromTime ? TimeUtils.dateToUTC(fromTime) : null;
    toTime = toTime ? TimeUtils.dateToUTC(toTime) : null;
    startTime = startTime ? TimeUtils.dateToUTC(startTime) : null;

    // Create time scale
    this.xScale = d3.scaleUtc().domain([fromTime, toTime]).range([0, 800]);

    // Set initial state
    this.state = {
      currentTime: startTime
        ? TimeUtils.dateToUTC(startTime)
        : TimeUtils.dateToUTC(fromTime),
      playbackSpeed: initialSpeed,
    };

    // Create a ref that can be used by d3 to draw axis
    this.axisRef = React.createRef();

    // Bind functions to component context
    this.forward = this.forward.bind(this);
    this.fastForward = this.fastForward.bind(this);
    this.backward = this.backward.bind(this);
    this.fastBackward = this.fastBackward.bind(this);
    this.doubleSpeed = this.doubleSpeed.bind(this);
    this.halfSpeed = this.halfSpeed.bind(this);
    this.play = this.play.bind(this);
    this.pause = this.pause.bind(this);
    this.stop = this.stop.bind(this);
  }

  componentWillUnmount() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  componentDidMount() {
    // Create time axis SVG
    var svg = d3
      .select(this.axisRef.current)
      .append("svg")
      .attr("viewBox", "-25, 0, 850, 35")
      .attr("preserveAspectRatio", "xMidYMid meet")
      .attr("height", 50)
      .attr("width", "100%");

    // Create axis
    var xAxis = d3
      .axisBottom()
      .scale(this.xScale)
      .tickFormat(NumUtils.multiFormat);

    // Append axis to SVG
    svg.append("g").attr("transform", "translate(0, 15)").call(xAxis);

    // Set that=this to access React context
    let that = this;

    // Define callback function in the context of "that"
    function dragged(d) {
      if (d3.event.x >= 0 && d3.event.x <= 800) {
        d3.select(this).attr("cx", d3.event.x);
        var timeMarkerLocation = that.xScale.invert(d3.event.x);
        that.setState({
          currentTime: timeMarkerLocation,
        });
        that.move(timeMarkerLocation, true);
      }
    }

    // Create time marker and make it "draggable"
    this.timeMarker = svg
      .append("circle")
      .attr("cx", 0)
      .attr("cy", 15)
      .attr("r", 5)
      .style("fill", "#fff")
      .style("stroke", "#000")
      .style("stroke-width", 2)
      .call(d3.drag().on("drag", dragged));
  }

  // Get positions
  getGeoJson(currentTime) {
    const { chartData } = this.props;
    return PosUtils.getPositionsUpToTime(chartData.allFiltered(), currentTime);
  }

  forward() {
    this.move(+2);
  }

  fastForward() {
    this.move(+30);
  }

  backward() {
    this.move(-2);
  }

  fastBackward() {
    this.move(-30);
  }

  doubleSpeed() {
    const { playbackSpeed } = this.state;
    this.setState({
      playbackSpeed: playbackSpeed * 2,
    });
  }

  halfSpeed() {
    const { playbackSpeed } = this.state;
    this.setState({
      playbackSpeed: playbackSpeed / 2 > 1 ? playbackSpeed / 2 : 1,
    });
  }

  move(offsetOrDate, isDate = false) {
    const { setGeoJson } = this.props;
    const { currentTime } = this.state;
    var geoJsonData;
    if (!isDate) {
      var sec = Math.floor(offsetOrDate);
      var ms = (offsetOrDate % 1) * 1000;
      var tmpDate = new Date(currentTime);
      tmpDate.setSeconds(
        tmpDate.getSeconds() + sec,
        tmpDate.getMilliseconds() + ms
      );
      this.setState({
        currentTime: tmpDate,
      });
      this.timeMarker.attr("cx", this.xScale(currentTime));
      geoJsonData = this.getGeoJson(currentTime);
    } else {
      this.setState({
        currentTime: new Date(offsetOrDate),
      });
      this.timeMarker.attr("cx", this.xScale(offsetOrDate));
      geoJsonData = this.getGeoJson(offsetOrDate);
    }
    setGeoJson(geoJsonData);
  }

  play() {
    const { toTime, framesPerSecond } = this.props;
    const { currentTime, playbackSpeed } = this.state;

    if (currentTime >= toTime) {
      this.stop();
    } else {
      this.move(playbackSpeed / framesPerSecond);
      this.timeout = setTimeout(() => {
        this.play();
      }, 1000 / framesPerSecond);
    }
  }

  stop() {
    const { fromTime, startTime } = this.props;
    if (this.timeout !== null) {
      clearTimeout(this.timeout);
    }
    if (startTime) {
      this.move(startTime, true);
    } else {
      this.move(fromTime, true);
    }
  }

  pause() {
    if (this.timeout !== null) {
      clearTimeout(this.timeout);
    }
  }

  render() {
    const { currentTime, playbackSpeed } = this.state;
    const { useFlex } = this.props;

    let aviTimeLineStyle = useFlex
      ? {
          flex: 1,
        }
      : {
          width: "100%",
        };

    return (
      <div className="avi-time-line" style={aviTimeLineStyle}>
        <div className="controls-container">
          <AVIActionButton
            label="Tilbake 30 sekund"
            onClick={this.fastBackward}
          />
          <AVIActionButton label="Spill av" onClick={this.play} />
          <AVIActionButton label="Pause" onClick={this.pause} />
          <AVIActionButton label="Stopp" onClick={this.stop} />
          <AVIActionButton
            label="Fremover 30 sekund"
            onClick={this.fastForward}
          />
        </div>
        <div className="axis-container">
          <div className="axis" ref={this.axisRef} />
        </div>
        <div className="axis-footer">
          Tidspunkt: {TimeUtils.formatTime(currentTime)} (UTC) &bull; hastighet
          : {playbackSpeed}x &bull;{" "}
          <button onClick={this.doubleSpeed}>øk</button> &bull;{" "}
          <button onClick={this.halfSpeed}>reduser</button>
        </div>
      </div>
    );
  }
}

TimeLine.propTypes = {
  fromTime: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])
    .isRequired,
  toTime: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])
    .isRequired,
  chartData: PropTypes.object.isRequired,
  setGeoJson: PropTypes.func.isRequired,
  startTime: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])
    .isRequired,
  framesPerSecond: PropTypes.number,
  initialSpeed: PropTypes.number,
  useFlex: PropTypes.bool,
};

TimeLine.defaultProps = {
  framesPerSecond: 20,
  initialSpeed: 16,
  useFlex: false,
};

export default TimeLine;
